import { TypedEventEmitter } from '../utils/TypedEventEmitter';
import { NOOP_LOGGER, writeLog } from '../webrtc/logger';
import { createRecoveryConfig, exponentialBackoff } from './config';
import type {
  RecoveryConfig,
  RecoveryControllerOptions,
  RecoveryEvents,
  RecoveryState,
  RecoveryStatus,
} from './types';

const COPY: Readonly<Record<RecoveryState, string>> = Object.freeze({
  CONNECTED: 'Connected and sharing securely.',
  DEGRADED: 'The connection is unstable. We are keeping your session active.',
  RECONNECTING: 'Reconnecting… Your session is still available.',
  FAILED: 'We could not restore the connection. Please try joining again.',
});

export class RecoveryController extends TypedEventEmitter<RecoveryEvents> {
  readonly config: Readonly<RecoveryConfig>;

  private readonly logger;
  private readonly random: () => number;
  private readonly onlineEventTarget: EventTarget | null;
  private readonly networkChangeEventTarget: EventTarget | null;
  private readonly isOnline: () => boolean;
  private readonly unsubscribers: Array<() => void> = [];
  private signalingTimer: ReturnType<typeof setTimeout> | null = null;
  private iceTimer: ReturnType<typeof setTimeout> | null = null;
  private signalingInFlight = false;
  private iceRestartInFlight = false;
  private signalingAttempt = 0;
  private iceRestartAttempt = 0;
  private highLossSamples = 0;
  private recoveredLossSamples = 0;
  private lastConnectionState: RTCPeerConnectionState | null = null;
  private lastIceState: RTCIceConnectionState | null = null;
  private started = false;
  private terminated = false;
  private _status: RecoveryStatus = createStatus('RECONNECTING');

  constructor(private readonly options: RecoveryControllerOptions) {
    super();
    this.config = createRecoveryConfig(options.config);
    this.logger = options.logger ?? NOOP_LOGGER;
    this.random = options.random ?? Math.random;
    this.onlineEventTarget = options.onlineEventTarget ?? defaultOnlineTarget();
    this.networkChangeEventTarget =
      options.networkChangeEventTarget ?? defaultNetworkChangeTarget();
    this.isOnline = options.isOnline ?? defaultIsOnline;
  }

  get status(): RecoveryStatus {
    return this._status;
  }

  start(): void {
    if (this.started || this.terminated) return;
    this.started = true;
    this.lastConnectionState = this.options.peer.connection?.connectionState ?? null;
    this.lastIceState = this.options.peer.connection?.iceConnectionState ?? null;
    this.unsubscribers.push(
      this.options.signaling.on('statechange', ({ current }) => this.handleSignalingState(current)),
      this.options.peer.on('connectionstatechange', ({ state }) =>
        this.handleConnectionState(state),
      ),
      this.options.peer.on('iceconnectionstatechange', ({ state }) => this.handleIceState(state)),
      this.options.peer.on('peerleft', ({ reason }) => this.handlePeerLeft(reason)),
      this.options.peer.on('error', ({ error, fatal }) => {
        if (fatal && this.isActive()) {
          this.reportError(error);
          this.setStatus('RECONNECTING');
          this.scheduleIceRestart(0);
        }
      }),
    );
    if (this.options.metricsSource) {
      this.unsubscribers.push(
        this.options.metricsSource.subscribe((metrics) => this.observeMetrics(metrics)),
      );
    }
    this.onlineEventTarget?.addEventListener('online', this.handleOnline);
    this.onlineEventTarget?.addEventListener('offline', this.handleOffline);
    this.networkChangeEventTarget?.addEventListener('change', this.handleNetworkChange);

    if (!this.isOnline()) {
      this.setStatus('DEGRADED');
    } else if (this.options.signaling.state !== 'connected') {
      this.setStatus('RECONNECTING');
      this.scheduleSignalingReconnect(0);
    } else if (this.isMediaConnected()) {
      this.setStatus('CONNECTED');
    } else {
      this.setStatus('RECONNECTING');
    }
  }

  observeMetrics(metrics: { readonly packetLossPercentage: number | null }): void {
    const loss = metrics.packetLossPercentage;
    if (loss === null || this.terminated || !this.started) return;
    if (loss >= this.config.packetLossDegradedPercentage) {
      this.highLossSamples += 1;
      this.recoveredLossSamples = 0;
      if (
        this.highLossSamples >= this.config.packetLossDegradedSamples &&
        this._status.state === 'CONNECTED'
      ) {
        this.setStatus('DEGRADED');
      }
      return;
    }
    this.highLossSamples = 0;
    if (loss <= this.config.packetLossRecoveryPercentage) {
      this.recoveredLossSamples += 1;
      if (
        this.recoveredLossSamples >= this.config.packetLossRecoverySamples &&
        this.isFullyConnected()
      ) {
        this.setStatus('CONNECTED');
      }
    } else {
      this.recoveredLossSamples = 0;
    }
  }

  retryNow(): void {
    if (!this.started || this.terminated) return;
    this.signalingAttempt = 0;
    this.iceRestartAttempt = 0;
    this.clearRecoveryTimers();
    this.setStatus('RECONNECTING');
    if (this.options.signaling.state !== 'connected') this.scheduleSignalingReconnect(0);
    else this.scheduleIceRestart(0);
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    this.clearRecoveryTimers();
    for (const unsubscribe of this.unsubscribers.splice(0)) unsubscribe();
    this.onlineEventTarget?.removeEventListener('online', this.handleOnline);
    this.onlineEventTarget?.removeEventListener('offline', this.handleOffline);
    this.networkChangeEventTarget?.removeEventListener('change', this.handleNetworkChange);
  }

  async terminate(): Promise<void> {
    if (this.terminated) return;
    this.terminated = true;
    this.stop();
    await this.options.peer.close({
      reason: this.options.role === 'host' ? 'host-ended' : 'left',
    });
    this.options.signaling.close({ forgetResumeToken: true });
    this.clearListeners();
  }

  private readonly handleOnline = () => {
    if (!this.isActive()) return;
    this.setStatus('RECONNECTING');
    if (this.options.signaling.state !== 'connected') this.scheduleSignalingReconnect(0);
    else this.scheduleIceRestart(0);
  };

  private readonly handleOffline = () => {
    if (!this.isActive()) return;
    this.clearIceTimer();
    this.setStatus('DEGRADED');
  };

  private readonly handleNetworkChange = () => {
    if (!this.isActive() || !this.isOnline()) return;
    this.setStatus('DEGRADED');
    this.scheduleIceRestart(this.config.networkChangeGraceMs);
  };

  private handleSignalingState(state: string): void {
    if (!this.isActive()) return;
    if (state === 'connected') {
      this.signalingAttempt = 0;
      this.clearSignalingTimer();
      try {
        this.options.peer.announceReady();
      } catch (cause) {
        this.reportError(asError('Could not resume the session handshake.', cause));
      }
      if (this.isMediaConnected()) this.setStatus('CONNECTED');
      else {
        this.setStatus('RECONNECTING');
        if (this.isMediaBroken()) this.scheduleIceRestart(0);
      }
      return;
    }
    if (state === 'disconnected' || state === 'failed') {
      this.setStatus(this.isMediaConnected() ? 'DEGRADED' : 'RECONNECTING');
      this.scheduleSignalingReconnect(this.nextSignalingDelay());
    } else if (state === 'connecting' || state === 'authenticating') {
      this.setStatus('RECONNECTING');
    } else if (state === 'closed') {
      this.failTerminal('The session has ended.');
    }
  }

  private handleConnectionState(state: RTCPeerConnectionState): void {
    if (!this.isActive()) return;
    this.lastConnectionState = state;
    switch (state) {
      case 'connected':
        this.markConnectedIfReady();
        return;
      case 'disconnected':
        this.setStatus('DEGRADED');
        this.scheduleIceRestart(this.config.disconnectedGraceMs);
        return;
      case 'failed':
        this.setStatus('RECONNECTING');
        this.scheduleIceRestart(0);
        return;
      case 'connecting':
        this.setStatus('RECONNECTING');
        return;
      case 'closed':
        this.failTerminal('The session has ended.');
        return;
      case 'new':
        return;
    }
  }

  private handleIceState(state: RTCIceConnectionState): void {
    if (!this.isActive()) return;
    this.lastIceState = state;
    switch (state) {
      case 'connected':
      case 'completed':
        this.markConnectedIfReady();
        return;
      case 'disconnected':
        this.setStatus('DEGRADED');
        this.scheduleIceRestart(this.config.disconnectedGraceMs);
        return;
      case 'failed':
        this.setStatus('RECONNECTING');
        this.scheduleIceRestart(0);
        return;
      case 'checking':
        this.setStatus('RECONNECTING');
        return;
      case 'new':
      case 'closed':
        return;
    }
  }

  private handlePeerLeft(reason: string): void {
    const message =
      reason === 'host-ended'
        ? 'The host ended this session.'
        : reason === 'session-expired'
          ? 'This session has expired.'
          : reason === 'kicked'
            ? 'You were removed from this session.'
            : 'The other participant left the session.';
    this.failTerminal(message);
  }

  private scheduleSignalingReconnect(delayMs: number): void {
    if (
      !this.isActive() ||
      !this.isOnline() ||
      this.signalingTimer !== null ||
      this.signalingInFlight
    )
      return;
    if (this.signalingAttempt >= this.config.maximumSignalingAttempts) {
      this.failTerminal(COPY.FAILED);
      return;
    }
    this.signalingTimer = setTimeout(() => {
      this.signalingTimer = null;
      void this.reconnectSignaling();
    }, delayMs);
  }

  private async reconnectSignaling(): Promise<void> {
    if (!this.isActive() || this.signalingInFlight || !this.isOnline()) return;
    if (this.options.signaling.state === 'connected') return;
    if (this.signalingAttempt >= this.config.maximumSignalingAttempts) {
      this.failTerminal(COPY.FAILED);
      return;
    }
    this.signalingInFlight = true;
    this.signalingAttempt += 1;
    this.setStatus('RECONNECTING', this.signalingAttempt);
    let failed = false;
    try {
      await this.options.signaling.connect();
    } catch (cause) {
      failed = true;
      this.reportError(asError('The session service could not reconnect.', cause));
    } finally {
      this.signalingInFlight = false;
    }
    if (failed && this.isActive()) {
      this.scheduleSignalingReconnect(this.nextSignalingDelay());
    }
  }

  private scheduleIceRestart(delayMs: number): void {
    if (!this.isActive() || this.iceTimer !== null || this.iceRestartInFlight) return;
    if (this.iceRestartAttempt >= this.config.maximumIceRestartAttempts && delayMs === 0) {
      this.failTerminal(COPY.FAILED);
      return;
    }
    this.iceTimer = setTimeout(() => {
      this.iceTimer = null;
      void this.restartIce();
    }, delayMs);
  }

  private async restartIce(): Promise<void> {
    if (!this.isActive() || this.iceRestartInFlight) return;
    if (!this.isOnline()) {
      this.setStatus('DEGRADED');
      return;
    }
    if (this.options.signaling.state !== 'connected') {
      this.scheduleSignalingReconnect(0);
      return;
    }
    if (this.iceRestartAttempt >= this.config.maximumIceRestartAttempts) {
      this.failTerminal(COPY.FAILED);
      return;
    }

    this.iceRestartInFlight = true;
    this.iceRestartAttempt += 1;
    const forceRelay = this.iceRestartAttempt >= this.config.relayFallbackAttempt;
    this.setStatus('RECONNECTING', this.iceRestartAttempt, forceRelay);
    try {
      const result = await this.options.peer.restartIce({ forceRelay });
      writeLog(this.logger, 'info', 'recovery', 'ice-restart.requested', {
        attempt: this.iceRestartAttempt,
        relayForced: result.relayForced,
      });
    } catch (cause) {
      this.reportError(asError('A connection recovery attempt failed.', cause));
    } finally {
      this.iceRestartInFlight = false;
    }

    if (this.isActive() && !this.isMediaConnected()) {
      const delay = Math.max(
        this.config.iceRestartTimeoutMs,
        exponentialBackoff(
          this.iceRestartAttempt + 1,
          this.config.iceInitialBackoffMs,
          this.config,
          this.random,
        ),
      );
      this.scheduleIceRestart(delay);
    }
  }

  private markConnectedIfReady(): void {
    if (!this.isFullyConnected()) return;
    this.clearIceTimer();
    this.iceRestartAttempt = 0;
    this.highLossSamples = 0;
    this.recoveredLossSamples = 0;
    this.setStatus('CONNECTED');
  }

  private nextSignalingDelay(): number {
    return exponentialBackoff(
      Math.max(1, this.signalingAttempt),
      this.config.signalingInitialBackoffMs,
      this.config,
      this.random,
    );
  }

  private isMediaConnected(): boolean {
    const connectionState =
      this.lastConnectionState ?? this.options.peer.connection?.connectionState ?? null;
    const connectionConnected =
      connectionState === 'connected' ||
      (connectionState === null && this.options.peer.state === 'connected');
    const iceConnected =
      this.lastIceState === null ||
      this.lastIceState === 'new' ||
      this.lastIceState === 'connected' ||
      this.lastIceState === 'completed';
    return connectionConnected && iceConnected;
  }

  private isMediaBroken(): boolean {
    const state = this.lastConnectionState ?? this.options.peer.connection?.connectionState;
    return (
      state === 'failed' ||
      state === 'disconnected' ||
      this.lastIceState === 'failed' ||
      this.lastIceState === 'disconnected' ||
      this.options.peer.state === 'failed'
    );
  }

  private isFullyConnected(): boolean {
    return (
      this.isOnline() && this.options.signaling.state === 'connected' && this.isMediaConnected()
    );
  }

  private isActive(): boolean {
    return this.started && !this.terminated && this._status.state !== 'FAILED';
  }

  private setStatus(
    state: RecoveryState,
    attempt = 0,
    usingRelayFallback = false,
    message = COPY[state],
  ): void {
    if (
      this._status.state === state &&
      this._status.attempt === attempt &&
      this._status.usingRelayFallback === usingRelayFallback &&
      this._status.message === message
    )
      return;
    const previous = this._status;
    const current = createStatus(state, attempt, usingRelayFallback, message);
    this._status = current;
    writeLog(this.logger, state === 'FAILED' ? 'error' : 'info', 'recovery', 'state.changed', {
      previous: previous.state,
      current: current.state,
      attempt,
      usingRelayFallback,
    });
    this.emit('statechange', { previous, current });
  }

  private failTerminal(message: string): void {
    this.clearRecoveryTimers();
    this.setStatus(
      'FAILED',
      Math.max(this.signalingAttempt, this.iceRestartAttempt),
      false,
      message,
    );
  }

  private reportError(error: Error): void {
    writeLog(this.logger, 'warn', 'recovery', 'attempt.failed', { name: error.name });
    this.emit('error', { error });
  }

  private clearRecoveryTimers(): void {
    this.clearSignalingTimer();
    this.clearIceTimer();
  }

  private clearSignalingTimer(): void {
    if (this.signalingTimer !== null) clearTimeout(this.signalingTimer);
    this.signalingTimer = null;
  }

  private clearIceTimer(): void {
    if (this.iceTimer !== null) clearTimeout(this.iceTimer);
    this.iceTimer = null;
  }
}

function createStatus(
  state: RecoveryState,
  attempt = 0,
  usingRelayFallback = false,
  message = COPY[state],
): RecoveryStatus {
  return Object.freeze({ state, message, attempt, usingRelayFallback });
}

function defaultOnlineTarget(): EventTarget | null {
  return typeof globalThis.window === 'undefined' ? null : globalThis.window;
}

function defaultNetworkChangeTarget(): EventTarget | null {
  if (typeof globalThis.navigator === 'undefined') return null;
  const connection = (globalThis.navigator as Navigator & { readonly connection?: unknown })
    .connection;
  return connection instanceof EventTarget ? connection : null;
}

function defaultIsOnline(): boolean {
  return typeof globalThis.navigator === 'undefined' || globalThis.navigator.onLine;
}

function asError(message: string, cause: unknown): Error {
  return cause instanceof Error ? new Error(message, { cause }) : new Error(message);
}
